import { createContext, useState, useMemo, useCallback } from 'react'
import { node } from 'prop-types'
import { api } from 'api'
import { t } from '@lingui/core/macro'

export const fieldSetContext = createContext({
  saveFieldSet: _fieldset => {},
  byId: {},
})

export const FieldSetProvider = ({ children }) => {
  const [byId, setById] = useState({})
  const [fieldsById, setFieldsById] = useState({})
  const [fieldsetLoading, setFieldsetLoading] = useState(true)

  const fetchFieldSets = useCallback(
    () =>
      api.get(`/api/v1/account/fieldsets/`).then(({ data }) => {
        setById(prev => data.reduce((acc, row) => ({ ...acc, [row.id]: row }), prev))
        return data
      }),
    [],
  )

  const fetchFieldSet = useCallback(
    id =>
      api.get(`/api/v1/account/fieldsets/${id}`).then(({ data }) => {
        setById(prev => ({ ...prev, [id]: { ...prev[id], ...data } }))
        return data
      }),
    [],
  )

  const fetchDuplicateFieldSet = useCallback(id => {
    api.get(`/api/v1/account/fieldsets/${id}`).then(({ data }) => {
      const name = data.name
      const cleanData = {
        ...data,
        id: 'new',
        name: t`Duplicate of ${name}`,
        fields: data.fields.map(field => {
          field.transformation && delete field.transformation.id
          field.validation && delete field.validation.id
          field.list_governance && delete field.list_governance.id
          field.list_governance && delete field.list_governance.governable_id
          // for now we have to delete the concatenation_governance because it requires a save first.
          if (field.concatenation_governance) {
            field.governance_type = null
            field.concatenation_governance = null
            // field.concatenation_governance = {concatenation_governance_patterns: [], field_id: null, separator: null, type: "" }
          }
          // same with autofill
          if (field.autofill) {
            field.governance_type = null
            field.autofill = null
            // field.autofill = {field_id: null, governable_id: null, governable_type: "", id: null}
          }
          if (field.transformation && field.transformation.calculated) {
            delete field.transformation.calculated
          }
          field.date && delete field.date.id

          return {
            ...field,
            id: `TEMP_${field.id}`,
            fieldset_id: null,
          }
        }),
      }

      setById(prev => ({ ...prev, new: cleanData }))
      return data
    })
  }, [])

  const setNewFieldSet = useCallback(() => {
    setById({ ...byId, new: { id: 'new', name: '', fields: [], targets: [] } })
  }, [byId, setById])

  const fetchAllFieldSetsAndFields = useMemo(
    () => () =>
      api.get(`/api/v1/account/fieldsets/populated`).then(({ data }) => {
        setById(() => data.reduce((acc, row) => ({ ...acc, [row.id]: row }), {}))
        setFieldsById(
          data.reduce((memo, fs) => {
            return fs.fields.reduce((acc, field) => {
              acc[field.id] = field
              return acc
            }, memo)
          }, {}),
        )
        setFieldsetLoading(false)
        return data
      }),
    [],
  )

  const saveFieldSet = useCallback(fieldSet => {
    const copy = {
      ...fieldSet,
      id: fieldSet.id === 'new' ? null : fieldSet.id,
      fields:
        fieldSet.id === 'new'
          ? fieldSet.fields.map(field => {
              const { id: _, ...rest } = field
              return rest
            })
          : fieldSet.fields,
    }
    return api
      .put(`/api/v1/account/fieldsets/`, copy)
      .then(({ data }) => {
        setById(prev => ({ ...prev, [data.id]: data }))
        const fieldIds = data.fields.reduce((acc, field) => {
          acc[field.id] = field
          return acc
        }, {})
        setFieldsById(prevState => ({ ...prevState, ...fieldIds }))
        return data
      })
      .catch(error => {
        throw error
      })
  }, [])

  const updateFieldsets = newFieldsets => {
    setById(newFieldsets)
  }

  const removeNewFieldSet = useCallback(() => {
    setById(old => {
      const { new: _deleted, ...rest } = old
      return rest
    })
  }, [])

  const value = useMemo(
    () => ({
      byId,
      fieldsById,
      fetchFieldSets,
      fetchFieldSet,
      fetchAllFieldSetsAndFields,
      updateFieldsets,
      setNewFieldSet,
      fetchDuplicateFieldSet,
      removeNewFieldSet,
      saveFieldSet,
      fieldsetLoading,
    }),
    [
      byId,
      fieldsById,
      fetchFieldSets,
      fetchFieldSet,
      fetchAllFieldSetsAndFields,
      updateFieldsets,
      setNewFieldSet,
      fetchDuplicateFieldSet,
      removeNewFieldSet,
      saveFieldSet,
      fieldsetLoading,
    ],
  )

  return <fieldSetContext.Provider value={value}>{children}</fieldSetContext.Provider>
}

FieldSetProvider.propTypes = {
  children: node.isRequired,
}
